import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Check } from '@phosphor-icons/react';
import { defaultVariant } from '@/services/catalog';
import { formatINR } from '@/lib/money';

// Bottom-sheet customiser: variant + modifiers + live price. Framework-agnostic
// output (opts) is fed to the shared cart engine by the parent.
export const ProductCustomizer = ({ product, onClose, onConfirm }) => {
  const [variant, setVariant] = useState(defaultVariant(product));
  const [selected, setSelected] = useState(() => {
    // Pre-select default option for single-choice groups (or first option when required).
    const init = {};
    (product.modifier_groups || []).forEach((g) => {
      if (g.selection_type === 'single') {
        const def = (g.options || []).find((o) => o.is_default) || (g.required ? (g.options || [])[0] : null);
        if (def) init[g.id] = [def.id];
      }
    });
    return init;
  });
  const [notes, setNotes] = useState('');
  const [qty, setQty] = useState(1);

  const groups = product.modifier_groups || [];

  const toggleOption = (group, opt) => {
    setSelected((prev) => {
      const cur = prev[group.id] || [];
      if (group.selection_type === 'single') {
        return { ...prev, [group.id]: [opt.id] };
      }
      const exists = cur.includes(opt.id);
      if (exists) return { ...prev, [group.id]: cur.filter((x) => x !== opt.id) };
      const max = group.max_select || 99;
      if (cur.length >= max) return prev;
      return { ...prev, [group.id]: [...cur, opt.id] };
    });
  };

  const selectedModifiers = useMemo(() => {
    const out = [];
    groups.forEach((g) => {
      (selected[g.id] || []).forEach((oid) => {
        const opt = (g.options || []).find((o) => o.id === oid);
        if (opt) out.push({ id: opt.id, name: opt.name, price: opt.price_delta || 0 });
      });
    });
    return out;
  }, [selected, groups]);

  const base = variant?.price ?? product.effective_price ?? 0;
  const modSum = selectedModifiers.reduce((s, m) => s + (m.price || 0), 0);
  const unit = base + modSum;
  const total = unit * qty;

  const missingRequired = groups.filter((g) => g.required && (selected[g.id] || []).length < Math.max(1, g.min_select || 1));
  const canConfirm = missingRequired.length === 0;

  const confirm = () => {
    if (!canConfirm) return;
    onConfirm({
      qty,
      variant: variant ? { id: variant.id, name: variant.name, price: base } : null,
      modifiers: selectedModifiers,
      notes,
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="product-customizer"
      >
        <motion.div
          className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[88vh] flex flex-col overflow-hidden"
          initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative">
            {product.image_url && <img src={product.image_url} alt={product.name} className="w-full h-36 object-cover" />}
            <button onClick={onClose} data-testid="customizer-close" className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow">
              <X size={16} weight="bold" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            <div>
              <h3 className="font-heading text-xl font-bold text-gray-900">{product.name}</h3>
              {product.description && <p className="text-sm text-gray-500 mt-1">{product.description}</p>}
            </div>

            {/* Variants */}
            {(product.variants?.length || 0) > 1 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Choose size</p>
                <div className="space-y-2">
                  {product.variants.map((v) => (
                    <button
                      key={v.id}
                      data-testid={`variant-${v.id}`}
                      onClick={() => setVariant(v)}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border text-sm transition-colors ${variant?.id === v.id ? 'border-sspice bg-sspice/5' : 'border-gray-200'}`}
                    >
                      <span className="font-medium text-gray-800">{v.name || 'Regular'}</span>
                      <span className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{formatINR(v.price)}</span>
                        {variant?.id === v.id && <Check size={16} weight="bold" className="text-sspice" />}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Modifier groups */}
            {groups.map((g) => {
              const cur = selected[g.id] || [];
              const isMissing = missingRequired.some((m) => m.id === g.id);
              return (
                <div key={g.id} data-testid={`modgroup-${g.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500">{g.name}</p>
                    {g.required && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${isMissing ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>Required</span>}
                    <span className="text-[10px] text-gray-400">{g.selection_type === 'single' ? 'Pick 1' : `Pick up to ${g.max_select || '∞'}`}</span>
                  </div>
                  <div className="space-y-2">
                    {(g.options || []).map((o) => {
                      const on = cur.includes(o.id);
                      return (
                        <button
                          key={o.id}
                          data-testid={`option-${o.id}`}
                          onClick={() => toggleOption(g, o)}
                          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border text-sm transition-colors ${on ? 'border-sspice bg-sspice/5' : 'border-gray-200'}`}
                        >
                          <span className="font-medium text-gray-800">{o.name}</span>
                          <span className="flex items-center gap-2">
                            {o.price_delta ? <span className="text-xs text-gray-500">+{formatINR(o.price_delta)}</span> : null}
                            <span className={`w-4 h-4 rounded ${g.selection_type === 'single' ? 'rounded-full' : ''} border flex items-center justify-center ${on ? 'bg-sspice border-sspice' : 'border-gray-300'}`}>
                              {on && <Check size={11} weight="bold" className="text-white" />}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Notes */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Special request</p>
              <input
                data-testid="customizer-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. extra spicy, no onion"
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sspice/20"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 p-4 flex items-center gap-3">
            <div className="flex items-center gap-2 bg-gray-100 rounded-full px-1 py-1">
              <button data-testid="customizer-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-7 h-7 flex items-center justify-center text-gray-700"><Minus size={14} weight="bold" /></button>
              <span className="text-sm font-bold min-w-[18px] text-center">{qty}</span>
              <button data-testid="customizer-qty-plus" onClick={() => setQty((q) => q + 1)} className="w-7 h-7 flex items-center justify-center text-gray-700"><Plus size={14} weight="bold" /></button>
            </div>
            <button
              data-testid="customizer-add-btn"
              onClick={confirm}
              disabled={!canConfirm}
              className="flex-1 py-3 bg-sspice hover:bg-sspice-hover disabled:opacity-50 text-white font-bold rounded-full transition-colors flex items-center justify-center gap-2"
            >
              {canConfirm ? `Add · ${formatINR(total)}` : `Select ${missingRequired[0]?.name}`}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProductCustomizer;
